import {
    Box, Container, Typography, TypographyProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ShadowBGContainer } from '../../foundation/Containers';

// ─── legacy (keep for sub-components still using them) ───────────────────────

export const ElpContainer = styled(Container)`
    && {
        ${props => props.theme.breakpoints.down('md')} {
            padding-inline: 1.5rem;
        }

        ${props => props.theme.breakpoints.up('md')} {
            padding-inline: 0;
        }
    }
`;

export const ElpBgSection = styled(Box)`
    background: ${props => props.theme.palette.primary.extraLight};
    border-radius: 1.5rem;
    padding: 2.5rem 2rem;
    border: 1px solid ${props => props.theme.palette.primary.main}20;

    ${props => props.theme.breakpoints.up('md')} {
        padding: 3rem 2.5rem;
    }
`;

export const ElpCardSection = styled(ShadowBGContainer)`
    ${props => props.theme.breakpoints.up('md')} {
        padding-inline: 1.5rem;
    }
`;

export const FeatureIconWrap = styled(Box)`
    width: 3.5rem;
    height: 3.5rem;
    flex-shrink: 0;
    background: ${props => props.theme.palette.primary.extraLight};
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.palette.primary.main};
    border: 1px solid ${props => props.theme.palette.primary.main}1a;
`;

export const MatchScore = styled('div')<{ scoreColor?: string }>`
    width: 3rem;
    height: 3rem;
    border-radius: 9999px;
    border: 3px solid ${props => props.scoreColor || props.theme.palette.primary.main};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.scoreColor || props.theme.palette.primary.main};
    font-weight: 700;
    font-size: 0.875rem;
    background: white;
`;

export const HeroCtaRow = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;

    ${props => props.theme.breakpoints.up('sm')} {
        flex-direction: row;
        justify-content: center;
    }
`;

export const DottedLine = styled('div')<{ height?: string }>`
    height: ${props => props.height || '2.5rem'};
    border-right: 2.5px dotted ${props => props.theme.palette.secondary.light || '#8e9eba'};
    width: 1px;
    margin: 0 auto;
    flex-shrink: 0;
`;

export const DottedLineHorizontal = styled('div')<{ width?: string }>`
    width: ${props => props.width || '4rem'};
    border-top: 2.5px dotted ${props => props.theme.palette.secondary.light || '#8e9eba'};
    height: 1px;
    flex-shrink: 0;
    align-self: center;
`;

// ─── how-it-works sub-components ─────────────────────────────────────────────

export const FlowContainer = styled('div')`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 21.25rem;
    margin: 0 auto;

    ${props => props.theme.breakpoints.down('md')} {
        padding-inline: 0.5rem;
    }

    ${props => props.theme.breakpoints.up('md')} {
        display: none;
    }
`;

export const DesktopFlowRow = styled(Box)`
    display: none;

    ${props => props.theme.breakpoints.up('md')} {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2rem;
        width: 100%;
    }
`;

export const StepCard = styled(Box)`
    width: 12.5rem;
    margin: 0 auto;
    background: white;
    border-radius: 1rem;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
    padding: 1.75rem 1.25rem 1.25rem;
    text-align: center;
    position: relative;
    border: 1px solid #f3f4f6;
`;

export const MiddleStepCard = styled(Box)`
    width: 100%;
    max-width: 14rem;
    margin: 0 auto;
    background: white;
    border-radius: 1rem;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
    padding: 1.75rem 1rem 1rem;
    text-align: center;
    position: relative;
    border: 1px solid #f3f4f6;

    ${props => props.theme.breakpoints.up('md')} {
        max-width: 16rem;
        margin: 0;
        flex-shrink: 0;
    }
`;

export const ChannelsList = styled(Box)`
    display: flex;
    flex-direction: column;
    margin-top: 0.75rem;
`;

export const ChannelItem = styled(Box)`
    padding: 0.75rem 0;

    &:not(:last-of-type) {
        border-bottom: 1px solid #f3f4f6;
    }
`;

export const DesktopStepCard = styled(StepCard)`
    width: 12.5rem;
    flex-shrink: 0;
    margin: 0;
`;

export const StepBadge = styled('div')`
    position: absolute;
    top: -1rem;
    left: 0;
    right: 0;
    margin-inline: auto;
    width: 2.25rem;
    height: 2.25rem;
    background: ${props => props.theme.palette.primary.main};
    color: white;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.25rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

// ─── legacy hero (kept for any remaining usages) ──────────────────────────────

export const HeroSection = styled(Box)`
    text-align: center;
    margin-bottom: 2.5rem;
`;

const HeroTitleTypography = (props: TypographyProps) => <Typography component="h1" {...props} />;
const HeroSubtitleTypography = (props: TypographyProps) => <Typography component="p" {...props} />;

export const HeroTitle = styled(HeroTitleTypography)(({ theme }) => ({
    '&&': {
        fontSize: '2.5rem',
        fontWeight: 700,
        letterSpacing: '-0.025em',
        marginBottom: '1rem',
        lineHeight: 1.2,
    },
    [theme.breakpoints.up('md')]: { '&&': { fontSize: '3rem' } },
}));

export const HeroSubtitle = styled(HeroSubtitleTypography)(({ theme }) => ({
    '&&': {
        maxWidth: '32rem',
        marginInline: 'auto',
        lineHeight: 1.625,
        fontSize: '0.9375rem',
    },
    [theme.breakpoints.up('md')]: { '&&': { maxWidth: 'none', fontSize: '1rem' } },
}));

export const FeaturesSection = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    ${props => props.theme.breakpoints.up('md')} {
        flex-direction: row;
        align-items: center;
        gap: 3rem;
    }
`;

export const FeaturesList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    flex: 1;
`;

export const FeaturesIllustration = styled('img')`
    display: none;

    ${props => props.theme.breakpoints.up('md')} {
        display: block;
        flex: 1;
        max-width: 50%;
        height: auto;
        object-fit: contain;
    }
`;

// ─── new design components ────────────────────────────────────────────────────

export const HeroBg = styled(Box)`
    background: linear-gradient(
        145deg,
        ${props => props.theme.palette.secondary.dark || '#051a29'} 0%,
        ${props => props.theme.palette.secondary.main} 55%,
        #0d3d28 100%
    );
    padding: 5rem 1.5rem 5.5rem;
    text-align: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -100px;
        right: -80px;
        width: 480px;
        height: 480px;
        background: radial-gradient(circle, ${props => props.theme.palette.primary.main}25 0%, transparent 65%);
        border-radius: 50%;
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: -60px;
        left: -40px;
        width: 320px;
        height: 320px;
        background: radial-gradient(circle, ${props => props.theme.palette.primary.main}18 0%, transparent 65%);
        border-radius: 50%;
        pointer-events: none;
    }

    ${props => props.theme.breakpoints.down('sm')} {
        padding: 3.5rem 1.25rem 4rem;
    }
`;

export const HeroBadge = styled(Box)`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 9999px;
    padding: 0.4rem 1.25rem;
    margin-bottom: 1.75rem;
    color: rgba(255, 255, 255, 0.92);
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    backdrop-filter: blur(4px);
`;

export const HeroHeadline = styled('h1')`
    color: white;
    font-weight: 800;
    line-height: 1.1;
    font-size: 2.75rem;
    margin: 0 0 1.25rem;
    letter-spacing: -0.03em;
    font-family: 'Open Sans Hebrew', sans-serif;

    ${props => props.theme.breakpoints.up('md')} {
        font-size: 4rem;
    }
`;

export const HeroSubText = styled('p')`
    color: rgba(255, 255, 255, 0.76);
    max-width: 38rem;
    margin: 0 auto 0;
    line-height: 1.75;
    font-size: 1rem;
    font-family: 'Open Sans Hebrew', sans-serif;

    ${props => props.theme.breakpoints.up('md')} {
        font-size: 1.125rem;
    }
`;

export const FeatureCardGrid = styled(Box)`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;

    ${props => props.theme.breakpoints.up('sm')} {
        grid-template-columns: repeat(3, 1fr);
    }
`;

export const FeatureCard = styled(Box)`
    background: white;
    border-radius: 1.25rem;
    padding: 2rem 1.5rem 1.75rem;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
    border: 1px solid ${props => props.theme.palette.primary.main}22;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg,
            ${props => props.theme.palette.primary.light},
            ${props => props.theme.palette.primary.main}
        );
        border-radius: 1.25rem 1.25rem 0 0;
    }

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 16px 48px ${props => props.theme.palette.primary.main}1a;
    }
`;

export const LargeFeatureIconWrap = styled(Box)`
    width: 3.75rem;
    height: 3.75rem;
    background: ${props => props.theme.palette.primary.extraLight};
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.palette.primary.main};
    margin-bottom: 1.25rem;
    font-size: 1.5rem;
`;

export const DemoSection = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 2rem;

    ${props => props.theme.breakpoints.up('md')} {
        flex-direction: row;
        align-items: stretch;
        gap: 2.5rem;
    }
`;

export const MidPageCta = styled(Box)`
    background: ${props => props.theme.palette.primary.extraLight};
    border-radius: 1.5rem;
    padding: 3.5rem 2rem;
    text-align: center;
    border: 1px solid ${props => props.theme.palette.primary.main}30;

    ${props => props.theme.breakpoints.down('sm')} {
        padding: 2.25rem 1.25rem;
        border-radius: 1rem;
    }
`;

export const ClosingCta = styled(Box)`
    background: linear-gradient(
        145deg,
        ${props => props.theme.palette.secondary.dark || '#051a29'} 0%,
        ${props => props.theme.palette.secondary.main} 55%,
        #0d3d28 100%
    );
    border-radius: 1.5rem;
    padding: 4.5rem 2rem;
    text-align: center;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: -70px;
        right: -70px;
        width: 280px;
        height: 280px;
        background: radial-gradient(circle, ${props => props.theme.palette.primary.main}30 0%, transparent 65%);
        border-radius: 50%;
        pointer-events: none;
    }

    &::after {
        content: '';
        position: absolute;
        bottom: -50px;
        left: -50px;
        width: 220px;
        height: 220px;
        background: radial-gradient(circle, ${props => props.theme.palette.primary.main}20 0%, transparent 65%);
        border-radius: 50%;
        pointer-events: none;
    }

    ${props => props.theme.breakpoints.down('sm')} {
        padding: 3rem 1.5rem;
        border-radius: 1rem;
    }
`;

export const SectionLabel = styled(Typography)`
    && {
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: ${props => props.theme.palette.primary.main};
        margin-bottom: 0.5rem;
    }
`;

export const SectionTitle = styled(Typography)`
    && {
        font-weight: 700;
        font-size: 1.875rem;
        line-height: 1.2;
        color: ${props => props.theme.palette.secondary.main};
        margin-bottom: 0.5rem;

        ${props => props.theme.breakpoints.up('md')} {
            font-size: 2.25rem;
        }
    }
`;

export const SectionSubtitle = styled(Typography)`
    && {
        color: ${props => props.theme.palette.secondary.main};
        opacity: 0.6;
        line-height: 1.65;
        max-width: 36rem;
        margin-inline: auto;
    }
`;
